import type { GameEvent } from '../game/simulation/types';

type SampleId = 'bossImpact' | 'coreImpact' | 'cryoPulse' | 'kineticShot' | 'teslaArc';

const SAMPLE_URLS: Readonly<Record<SampleId, string>> = {
  bossImpact: '/assets/audio/explosionCrunch_003.ogg',
  coreImpact: '/assets/audio/impactMetal_003.ogg',
  cryoPulse: '/assets/audio/forceField_002.ogg',
  kineticShot: '/assets/audio/laserSmall_000.ogg',
  teslaArc: '/assets/audio/laserLarge_001.ogg',
};

export class AudioDirector {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private ambience: OscillatorNode[] = [];
  private readonly samples = new Map<SampleId, AudioBuffer>();
  private sampleLoad: Promise<void> | null = null;
  private muted = false;
  private lastShotAt = 0;
  private lastImpactAt = 0;
  private lastPickupAt = 0;
  private lastKillAt = 0;
  private killPitchStep = 0;

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.master && this.context) {
      this.master.gain.setTargetAtTime(muted ? 0 : 0.18, this.context.currentTime, 0.04);
    }
  }

  async start(): Promise<void> {
    if (this.muted || typeof AudioContext === 'undefined') return;
    this.context ??= new AudioContext();
    if (this.context.state === 'suspended') await this.context.resume();
    if (!this.master) {
      this.master = this.context.createGain();
      this.master.gain.value = this.muted ? 0 : 0.18;
      this.master.connect(this.context.destination);
    }
    if (this.ambience.length === 0) this.startAmbience();
    this.sampleLoad ??= this.loadSamples();
    await this.sampleLoad;
  }

  stop(): void {
    for (const oscillator of this.ambience) {
      try {
        oscillator.stop();
      } catch {
        // The browser may already have stopped a voice during page shutdown.
      }
    }
    this.ambience = [];
  }

  handle(events: readonly GameEvent[]): void {
    if (this.muted || !this.context || !this.master) return;
    for (const event of events) {
      if (event.type === 'projectileFired' && event.payload.family === 'kinetic') {
        if (this.context.currentTime - this.lastShotAt > 0.12) {
          this.lastShotAt = this.context.currentTime;
          this.sample('kineticShot', 0.15, 0.82);
          this.tone(150, 0.045, 'square', 0.035, 80);
        }
      }
      if (event.type === 'teslaFired') {
        this.sample('teslaArc', 0.13, 1.25);
        this.tone(540, 0.08, 'sawtooth', 0.035, 980);
      }
      if (event.type === 'cryoPulse') {
        this.sample('cryoPulse', 0.12, 0.75);
        this.tone(210, 0.16, 'sine', 0.04, 105);
      }
      if (event.type === 'coreDamaged' && this.context.currentTime - this.lastImpactAt > 0.16) {
        this.lastImpactAt = this.context.currentTime;
        this.sample('coreImpact', 0.17, 0.72);
        this.tone(75, 0.2, 'sawtooth', 0.06, 42);
      }
      if (event.type === 'enemyKilled' && this.context.currentTime - this.lastKillAt > 0.045) {
        this.lastKillAt = this.context.currentTime;
        this.killPitchStep = (this.killPitchStep + 1) % 9;
        const heavy =
          event.payload.kind === 'tank' ||
          event.payload.kind === 'protector' ||
          event.payload.kind === 'warden' ||
          event.payload.kind === 'devourer';
        const frequency = 105 + this.killPitchStep * 17;
        this.tone(
          frequency,
          heavy ? 0.11 : 0.065,
          heavy ? 'sawtooth' : 'triangle',
          heavy ? 0.032 : 0.018,
          frequency * 0.58,
        );
        if (event.payload.kind === 'warden' || event.payload.kind === 'devourer')
          this.sample('bossImpact', 0.24, 0.62);
      }
      if (event.type === 'synergyUnlocked') this.chord([261.6, 392, 523.2], 0.45, 0.08);
      if (event.type === 'evolutionUnlocked') this.chord([110, 220, 329.6, 440], 1.2, 0.12);
      if (event.type === 'bossPhase') {
        this.sample('bossImpact', 0.2, 0.72);
        this.chord([55, 82.4, 110], 0.7, 0.1);
      }
      if (event.type === 'bossEntered') {
        this.sample('bossImpact', 0.24, event.payload.kind === 'devourer' ? 0.58 : 0.78);
        this.chord([48.9, 73.4, 98], 0.9, 0.12);
      }
      if (event.type === 'coreAbilityActivated') {
        this.chord([196, 293.7, 392], 0.32, 0.06);
      }
      if (event.type === 'systemUpgradeActivated') {
        const base =
          event.payload.family === 'kinetic'
            ? 220
            : event.payload.family === 'tesla'
              ? 293.7
              : event.payload.family === 'cryo'
                ? 196
                : 261.6;
        const rankLift = 1 + (event.payload.rank - 1) * 0.25;
        this.chord(
          [base * rankLift, base * 1.25 * rankLift, base * 2 * rankLift],
          event.payload.rank === event.payload.maxRank ? 0.7 : 0.38,
          0.07,
        );
      }
      if (event.type === 'xpCollected') {
        const isPremium = event.payload.tier === 'gold' || event.payload.tier === 'prismatic';
        if (isPremium || this.context.currentTime - this.lastPickupAt > 0.055) {
          this.lastPickupAt = this.context.currentTime;
          const frequency =
            event.payload.tier === 'gray'
              ? 420
              : event.payload.tier === 'blue'
                ? 620
                : event.payload.tier === 'gold'
                  ? 880
                  : 1180;
          this.tone(
            frequency,
            isPremium ? 0.13 : 0.055,
            'sine',
            isPremium ? 0.05 : 0.018,
            frequency * 1.22,
          );
        }
      }
      if (event.type === 'bossArenaLocked') {
        this.sample('bossImpact', 0.18, 0.68);
        this.chord([55, 82.4, 123.5], 0.65, 0.08);
      }
      if (event.type === 'overdriveActivated') {
        this.chord([110, 220, 440, 880], 0.72, 0.1);
      }
      if (event.type === 'waveStarted' && event.payload.wave >= 8) {
        const lift = 1 + (event.payload.wave - 8) * 0.12;
        this.chord([110 * lift, 164.8 * lift, 220 * lift, 329.6 * lift], 0.6, 0.075);
      }
      if (event.type === 'runEnded') {
        this.chord(
          event.payload.result === 'victory' ? [261.6, 329.6, 392, 523.2] : [146.8, 138.6, 110],
          1.1,
          0.12,
        );
      }
    }
  }

  private startAmbience(): void {
    if (!this.context || !this.master) return;
    const ambienceGain = this.context.createGain();
    ambienceGain.gain.value = 0.09;
    ambienceGain.connect(this.master);
    for (const [frequency, type] of [
      [43.65, 'sine'],
      [87.31, 'triangle'],
    ] as const) {
      const oscillator = this.context.createOscillator();
      oscillator.type = type;
      oscillator.frequency.value = frequency;
      oscillator.detune.value = this.ambience.length === 0 ? -4 : 5;
      oscillator.connect(ambienceGain);
      oscillator.start();
      this.ambience.push(oscillator);
    }
  }

  private async loadSamples(): Promise<void> {
    if (!this.context) return;
    await Promise.all(
      (Object.entries(SAMPLE_URLS) as [SampleId, string][]).map(async ([id, url]) => {
        try {
          const response = await fetch(url);
          if (!response.ok) return;
          const buffer = await this.context?.decodeAudioData(await response.arrayBuffer());
          if (buffer) this.samples.set(id, buffer);
        } catch {
          // Runtime synthesis remains available if an asset cannot be decoded.
        }
      }),
    );
  }

  private sample(id: SampleId, volume: number, playbackRate: number): void {
    if (!this.context || !this.master) return;
    const buffer = this.samples.get(id);
    if (!buffer) return;
    const source = this.context.createBufferSource();
    const gain = this.context.createGain();
    source.buffer = buffer;
    source.playbackRate.value = playbackRate;
    gain.gain.value = volume;
    source.connect(gain);
    gain.connect(this.master);
    source.start();
  }

  private tone(
    frequency: number,
    duration: number,
    type: OscillatorType,
    volume: number,
    endFrequency: number,
  ): void {
    if (!this.context || !this.master) return;
    const now = this.context.currentTime;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, now);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(1, endFrequency), now + duration);
    gain.gain.setValueAtTime(volume, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain);
    gain.connect(this.master);
    oscillator.start(now);
    oscillator.stop(now + duration + 0.02);
  }

  private chord(frequencies: readonly number[], duration: number, volume: number): void {
    frequencies.forEach((frequency, index) => {
      window.setTimeout(
        () => this.tone(frequency, duration, 'sine', volume / frequencies.length, frequency * 1.01),
        index * 35,
      );
    });
  }
}
